import React, { useState } from 'react';

import { useCourse } from 'presentation/hooks/courses';
import { ToroLearnHeader } from 'presentation/components/header';
import { ToroLearnIcon } from 'presentation/components/icon';
import { ToroLearnButton } from 'presentation/components/button';
import { ToroLearnSideBar } from 'presentation/components/sidebar';
import { ToroLearnPlaylist } from 'presentation/components/playlist';
import { NextLessonBanner } from 'presentation/components/banner/next-lesson';

import './styles.scss';
import { environment } from 'utils/environment.utils';
import { usePlaylist } from 'presentation/hooks/playlist';
import { trackingEvent } from 'data/usecases/tracking-event';
import { ToroLearnBuyFooter } from 'presentation/components/buy-footer';
import ModalToroForm from 'presentation/components/modal';
import { useUser } from 'presentation/hooks/user';

/**
 * Página inicial - hero do curso
 *
 * @returns
 */
export const Home = (): JSX.Element => {
    const { setSideBarActive } = usePlaylist();
    const { course, isLocked } = useCourse();
    const [footerMenu, setFooterMenu] = useState(0);
    const { isRegistered: isRegisteredUser } = useUser();
    const [showToroFormModal, setShowToroFormModal] = useState(false);

    const footerNav = [
        { label: 'Resumo', isVisible: true },
        { label: 'Bônus', isVisible: !!course?.bonus.strategies, isLocked },
    ];

    const handleOpenLessons = (courseName: string) => {
        setSideBarActive(true);
        trackingEvent.elementClicked({
            productAreaOrigin: 'Aprenda',
            element: 'Botão Todas as aulas',
            group: 'Bônus',
            text: courseName,
            timeResponsavel: 'Sky',
        });
    };

    if (!course) return <span></span>;

    return (
        <main
            className="wrapper course"
            style={{
                backgroundImage: `linear-gradient(360deg, #22222200 29.55%, #22222200 95.83%), url(${course.coverUrl})`,
            }}
        >
            <div className="d-flex flex-column justify-content-between vh-100">
                <div className="wrapper-header">
                    <ToroLearnHeader />
                    <div className="bar d-flex justify-content-between align-items-center px-5 mt-5">
                        <a href={`${environment.APP_URL}/home/cursos`} className="link font-size-9">
                            <ToroLearnIcon icon="keyboard-arrow-left" size={14} fill="white" className="mr-3" />
                            Meus cursos
                        </a>

                        {footerMenu === 1 && (
                            <div>
                                <h3 className="h5 text-capitalize font-size-5 text-white">{course.name}</h3>
                                <p className="font-size-9 category-name text-right font-weight-light text-uppercase">
                                    {course.category.name}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="wrapper-content px-5 d-md-flex justify-content-between align-items-center text-white">
                    {footerMenu === 0 && (
                        <div className="left-box">
                            <p className="category-name font-size-9 font-weight-light text-uppercase">
                                {course.category.name}
                            </p>
                            <h1 className="text-capitalize font-size-1 mb-5">{course.name}</h1>
                            <ToroLearnButton
                                icon="playlist"
                                btnType="btn-outline-white"
                                onClick={() => handleOpenLessons(course.name)}
                            >
                                Todas aulas
                            </ToroLearnButton>
                        </div>
                    )}
                    {footerMenu === 1 && (
                        <>
                            <div className="left-box">
                                <h2 className="text-capitalize font-size-1 mb-3">{course.bonus.title}</h2>
                                <p className="text-gray-light font-size-8">{course.bonus.name}</p>
                            </div>
                            <div className="right-box d-flex">
                                {course.bonus.strategies.map(strategy => (
                                    <div key={strategy.id} className="card-bonus d-md-flex mr-3">
                                        <div className="card bg-transparent text-white">
                                            <img
                                                loading="lazy"
                                                className="card-img"
                                                src={`${strategy.coverUrl}`}
                                                alt="Card image"
                                            />
                                            <div className="card-img-overlay d-flex flex-column justify-content-end">
                                                <div className="card-content d-flex justify-content-between align-items-center">
                                                    <h4 className="card-title font-size-6 mb-0">{strategy.name}</h4>
                                                    <a
                                                        onClick={() =>
                                                            trackingEvent.elementClicked({
                                                                productAreaOrigin: 'Aprenda',
                                                                element: 'Botão Bônus',
                                                                group: 'Bônus',
                                                                step: strategy.name,
                                                                label: 'Acessar',
                                                                text: course.name,
                                                                timeResponsavel: 'Sky',
                                                            })
                                                        }
                                                        href={strategy.accessLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-secondary btn-sm"
                                                    >
                                                        Acessar
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="wrapper-footer px-5 d-flex align-items-center justify-content-between py-5 mb-5">
                    <ul className="nav">
                        {footerNav.map((item, index) => {
                            if (!item.isVisible) return;

                            return (
                                <li
                                    key={item.label}
                                    className={`nav-${item?.isLocked ? 'locked' : 'item'} ${
                                        index === footerMenu && 'active'
                                    }`}
                                >
                                    {item?.isLocked ? (
                                        <button
                                            className="nav-link d-flex align-items-center text-gray-graphite"
                                            type="button"
                                            disabled
                                        >
                                            <ToroLearnIcon icon="lock" fill="#49535C" size={12} />
                                            {item.label}
                                        </button>
                                    ) : (
                                        <button
                                            className="nav-link"
                                            type="button"
                                            onClick={() => {
                                                setFooterMenu(index);
                                                trackingEvent.elementClicked({
                                                    productAreaOrigin: 'Aprenda',
                                                    element: 'Tab Menu Curso',
                                                    group: 'Menu Curso',
                                                    label: item.label,
                                                    text: course.name,
                                                    timeResponsavel: 'Sky',
                                                });
                                            }}
                                        >
                                            {item.label}
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    {footerMenu === 1 && (
                        <ToroLearnButton
                            icon="playlist"
                            btnType="btn-outline-white"
                            onClick={() => handleOpenLessons(course.name)}
                        >
                            Todas as aulas
                        </ToroLearnButton>
                    )}
                </div>
            </div>

            {footerMenu === 0 && course.progress.percent !== 100 && (
                <NextLessonBanner
                    courseName={course.name}
                    moduleId={course.nextLesson.moduleId}
                    lessonId={course.nextLesson.lessonId}
                    courseSlug={course.slug}
                    thumbnailUrl={course.coverUrl}
                    lessonName={course.modules[course.nextLesson.moduleId].lessons[course.nextLesson.lessonId].name}
                    moduleName={course.modules[course.nextLesson.moduleId].name}
                />
            )}

            <ToroLearnSideBar isLocked={isLocked}>
                <ToroLearnPlaylist isSideBar />
            </ToroLearnSideBar>
            {isLocked && <ToroLearnBuyFooter setShowToroFormModal={setShowToroFormModal} />}
            {showToroFormModal && (
                <ModalToroForm
                    showModal={showToroFormModal}
                    setShowModal={setShowToroFormModal}
                    registeredUser={isRegisteredUser}
                />
            )}
        </main>
    );
};
